import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, useMediaQuery, useTheme } from "@mui/material";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import { useTranslation } from "react-i18next";
import { api } from "../utils/helpers";

export function PackageTopicButton({ item, disabled, onClick }) {
  const { t } = useTranslation("freigabe");
  if (!item) return null;
  const action = item.pending ? "packageTopic.retry" : (item.topicId || item.membershipState === "mixed") ? "packageTopic.change" : "packageTopic.assign";
  return <Button type="button" className="iio-monitor-button promotion-package-topic-action" startIcon={<SwapHorizRounded/>}
    disabled={disabled || !!item.pendingRetirement} aria-haspopup="dialog" aria-label={t("packageTopic.manageNamed", { name: item.packageName || item.packageId, action: t(action) })}
    onClick={() => onClick(item)}>{t(action)}</Button>;
}

export default function PromotionPackageTopic({ item, current, topics, spaceId, disabled, onStart, onEnd, onClose, onPendingChange }) {
  const { t } = useTranslation("freigabe");
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const id = useId();
  const [destination, setDestination] = useState(item.pendingOperation ? item.pendingOperation.topicId || "" : (item.membershipState === "mixed" ? "__mixed__" : item.topicId || ""));
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState(null);
  // Wartet der Server auf ein belegtes Repository (bis 60 s), sagt der Dialog das nach kurzer Zeit ehrlich an.
  const [slow, setSlow] = useState(false);
  useEffect(() => { if (!busy) { setSlow(false); return undefined; } const timer = setTimeout(() => setSlow(true), 4000); return () => clearTimeout(timer); }, [busy]);
  const writing = useRef(false);
  const payload = useRef(item.pendingOperation ? { spaceId, ...item.pendingOperation } : null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { onPendingChange?.(busy || uncertain); return () => onPendingChange?.(false); }, [busy, uncertain, onPendingChange]);
  const retrying = uncertain || !!current?.pendingOperation;
  const pendingElsewhere = current?.pending && !current.pendingOperation;
  const target = topics.find(topic => topic.id === destination);
  const changed = !current || current.revision !== item.revision;
  const unchanged = item.membershipState !== "mixed" && destination === (item.topicId || "");
  const valid = !disabled && !busy && !pendingElsewhere && (retrying || (!changed && !unchanged && (!destination || (target && !target.archived))));
  const close = () => { if (!busy) onClose(); };
  const submit = async () => {
    if (!valid || writing.current) return;
    if (!payload.current) payload.current = { spaceId, packageId: item.packageId, topicId: destination || null,
      revision: item.revision, ...(destination ? { topicRevision: target.revision } : {}), requestId: window.crypto.randomUUID() };
    writing.current = true; setBusy(true); setError(null); onStart();
    let done = false;
    try {
      const result = await api("/api/cicd/transport/promotions/package-topic", { method: "POST", timeout: 90000, body: JSON.stringify(payload.current) });
      if (result?.package?.packageId !== item.packageId || (result.package.topicId || null) !== payload.current.topicId)
        throw new Error("TOPIC_PACKAGE_RESPONSE_INVALID");
      done = true;
      if (alive.current) setUncertain(false);
    } catch (failure) {
      if (alive.current) {
        setError(failure.code === "BATCH_BUSY" ? t("packageTopic.busyAfterWait")
          : t(`packageTopic.errors.${failure.code}`, { defaultValue: t(`topics.errors.${failure.code}`, { defaultValue: t("packageTopic.writeError") }) }));
        const rejected = failure.status >= 400 && failure.status < 500 && ![408, 429].includes(failure.status);
        setUncertain(!rejected);
        // Keep the request identity until a confirmed read resolves a pending Git write.
      }
    } finally {
      try { await onEnd(); } finally {
        writing.current = false;
        if (alive.current) { setBusy(false); if (done) onClose(true); }
      }
    }
  };
  return <Dialog open fullWidth maxWidth="sm" fullScreen={mobile} onClose={close}
    PaperProps={{ className: "iio iio-monitor-dialog promotion-dialog promotion-package-topic-dialog" }} aria-labelledby={`${id}-title`} aria-describedby={`${id}-hint`}>
    <DialogTitle id={`${id}-title`}>{t("packageTopic.title")}</DialogTitle>
    <DialogContent>
      <p><strong>{item.packageName || item.packageId}</strong></p>
      {item.packageName && item.packageName !== item.packageId && <p className="promotion-muted">{item.packageId}</p>}
      <p id={`${id}-hint`}>{t("packageTopic.hint")}</p>
      <FormControl fullWidth margin="normal" size="small">
        <InputLabel shrink id={`${id}-destination`}>{t("packageTopic.destination")}</InputLabel>
        <Select displayEmpty labelId={`${id}-destination`} label={t("packageTopic.destination")} value={destination} disabled={busy || retrying || disabled || changed || pendingElsewhere}
          className="iio-monitor-select" MenuProps={{ PaperProps: { className: "iio iio-monitor-menu" } }}
          onChange={event => { setDestination(event.target.value); setError(null); payload.current = null; }}>
          <MenuItem value="">{t("packageTopic.standard")}</MenuItem>
          {item.membershipState === "mixed" && <MenuItem value="__mixed__" disabled>{t("packageTopic.mixed")}</MenuItem>}
          {item.topicId && !topics.some(topic => topic.id === item.topicId && !topic.archived) && <MenuItem value={item.topicId} disabled>{item.topicName || t("topics.unavailableSelection")}</MenuItem>}
          {topics.filter(topic => !topic.archived).map(topic => <MenuItem key={topic.id} value={topic.id}>{topic.name}</MenuItem>)}
        </Select>
      </FormControl>
      <p className="promotion-muted">{t("packageTopic.requestHint")}</p>
      {item.freeze?.frozen && <p>{t("packageTopic.freezeHint")}</p>}
      {changed && !retrying && <Alert severity="warning">{t("packageTopic.changed")}</Alert>}
      {disabled && !busy && <Alert severity="warning">{t("packageTopic.unavailable")}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {pendingElsewhere && <Alert severity="warning">{t("packageTopic.pendingElsewhere")}</Alert>}
      {retrying && <Alert severity="warning">{t("packageTopic.uncertain")}</Alert>}
      {busy && slow && <Alert severity="info">{t("packageTopic.waitingBusy")}</Alert>}
    </DialogContent>
    <DialogActions>
      <Button autoFocus type="button" className="iio-monitor-button" disabled={busy} onClick={close}>{t("topics.cancel")}</Button>
      <Button type="button" className="iio-monitor-button iio-btn--signal" disabled={!valid} onClick={submit}>{t(busy ? "promotion.sending" : retrying ? "promotion.resend" : "packageTopic.save")}</Button>
    </DialogActions>
  </Dialog>;
}
